import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_86053e75-6738-418d-9d60-d347c42f7069/artifacts/fc1a4u8i_Ativo%208.png";

const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: "/sobre", label: t.nav.about, id: "about" },
    { to: "/solucoes", label: t.nav.solutions, id: "solutions" },
    { to: "/setores", label: t.nav.sectors, id: "sectors" },
    { to: "/projetos", label: t.nav.projects, id: "projects" },
    { to: "/insights", label: t.nav.insights, id: "insights" },
    { to: "/contato", label: t.nav.contact, id: "contact" },
  ];

  return (
    <header
      className="sticky top-0 z-50 bg-synergia-green border-b border-synergia-green/60"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center shrink-0"
          data-testid="site-logo-link"
          onClick={() => setOpen(false)}
        >
          <img
            src={LOGO_URL}
            alt="SYNERGIA"
            className="h-10 md:h-12 w-auto object-contain"
            data-testid="site-logo"
          />
        </Link>

        <nav
          className="hidden lg:flex items-center gap-9"
          data-testid="desktop-nav"
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `font-inter text-[13px] tracking-wide link-underline transition-colors duration-300 ${
                  isActive
                    ? "text-synergia-yellow"
                    : "text-white/85 hover:text-synergia-yellow"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <div
            className="hidden sm:flex items-center gap-2 font-inter text-[12px] tracking-widest"
            data-testid="lang-switcher"
          >
            <button
              type="button"
              onClick={() => setLang("pt")}
              data-testid="lang-pt"
              className={`transition-colors duration-300 ${
                lang === "pt"
                  ? "text-synergia-yellow font-semibold"
                  : "text-white/60 hover:text-white"
              }`}
            >
              PT
            </button>
            <span className="text-white/30">/</span>
            <button
              type="button"
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`transition-colors duration-300 ${
                lang === "en"
                  ? "text-synergia-yellow font-semibold"
                  : "text-white/60 hover:text-white"
              }`}
            >
              EN
            </button>
          </div>

          <Link
            to="/contato"
            className="hidden md:inline-flex btn-primary !py-3 !px-5 text-[13px]"
            data-testid="header-cta"
          >
            {t.nav.contactCta}
          </Link>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-white/10 bg-synergia-green"
          data-testid="mobile-nav"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.id}`}
                className={`font-inter text-base ${
                  location.pathname === l.to
                    ? "text-synergia-yellow"
                    : "text-white/90"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <div className="flex items-center gap-3 pt-2" data-testid="mobile-lang-switcher">
              <button
                type="button"
                onClick={() => setLang("pt")}
                className={`text-[12px] tracking-widest ${
                  lang === "pt" ? "text-synergia-yellow font-semibold" : "text-white/60"
                }`}
                data-testid="mobile-lang-pt"
              >
                PT
              </button>
              <span className="text-white/30">/</span>
              <button
                type="button"
                onClick={() => setLang("en")}
                className={`text-[12px] tracking-widest ${
                  lang === "en" ? "text-synergia-yellow font-semibold" : "text-white/60"
                }`}
                data-testid="mobile-lang-en"
              >
                EN
              </button>
            </div>
            <Link
              to="/contato"
              onClick={() => setOpen(false)}
              className="btn-primary mt-2 justify-center"
              data-testid="mobile-header-cta"
            >
              {t.nav.contactCta}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
